import {Tabs, Tab, Grid} from '@mui/material';
import {useTranslation} from "react-i18next";
import {useNavigate} from 'react-router';
import {observer} from 'mobx-react';
import React from 'react';
import {TAB_TRIP, TAB_YEAR, TAB_LOC, TAB_ASSO, TAB_MONTH, TAB_TIME, TAB_LOCATION, TAB_SPEC, TAB_OTHER} from './StatsCommon.js';
import {useGlobalStorage} from '../mobx/GlobalStorage.js';
import StatsCompare, {getTabStr} from './StatsCompare.js';
import {monthNames, getCityName} from '../Trans.js'
import {LIST_MAP_MAX_ELEM} from '../ListMap.js';
import StatsLocation from './StatsLocation.js';
import StatsSpecies from './StatsSpecies.js';
import StatsMissing from './StatsMissing.js';
import StatsOther from './StatsOther.js';
import StatsTime from './StatsTime.js';
import StatsObs from './StatsObs.js';
import ObsAppBar from '../AppBar.js';
import {COMM_OK} from '../WsR2.js';

const Stats = observer((props) => {
	const gStorage = useGlobalStorage();
	const {t} = useTranslation('obs');
	const navigate = useNavigate();

	function getheader() {
		return t('menu.stats');
	}

	const handleTabChange = (event, tab) => {
//  		console.log('handleTabChange', tab);
		gStorage.stats.setObsList(null);
		gStorage.stats.setTab(tab);
	}

	function getLocDesc(id) {
		if (!id)
			return '';

		const loc = gStorage.lists.loclist.list.find(l => l.id === id);
		if (!loc)
			return '??';

		return loc.name;
	}

	function statsGetCityName(city_id) {
		const city = gStorage.lists.citylist.list.find(l => l.id === city_id);

		return getCityName(city, gStorage.conn.user);
	}

	function statsGetStatsDesc() {
		let desc = '';
		let tab = 0;

		if (gStorage.stats.tab === TAB_TIME) {
			tab = gStorage.stats.time_tab;
		} else if (gStorage.stats.tab === TAB_LOCATION) {
			tab = gStorage.stats.loc_tab;
		} else if (gStorage.stats.tab === TAB_SPEC) {
			tab = gStorage.stats.spec_tab;
		} else if (gStorage.stats.tab === TAB_OTHER) {
			tab = gStorage.stats.other_tab;
		}

		desc = t(getTabStr(tab));

		if (gStorage.stats.tab === TAB_TIME) {
			if (gStorage.stats.time_tab === TAB_YEAR) {
				desc += ': ' + gStorage.stats.year;
			} else if (gStorage.stats.time_tab === TAB_MONTH) {
				let m = gStorage.stats.month;

				if (m >= 1 && m <= 12) {
					desc += ': ' + t(monthNames[gStorage.stats.month - 1]);
				}
			}
		} else if (gStorage.stats.tab === TAB_OTHER && gStorage.stats.other_tab === TAB_TRIP) {
			desc += ': ' + gStorage.lists.triplist.list.find((trip) => trip.id === gStorage.stats.trip).name;
		} else if (gStorage.stats.tab === TAB_LOCATION) {
			if (gStorage.stats.loc_tab === TAB_LOC) {
				if (gStorage.stats.location >= 0) {
					desc += ': ' + getLocDesc(gStorage.stats.location);
				} else {
					desc += ': ' + statsGetCityName(-gStorage.stats.location);
				}
			} else if (gStorage.stats.loc_tab === TAB_ASSO) {
				desc += ': ' + gStorage.lists.assolist.list.find(l => l.id === gStorage.stats.association).nick;
			}
		}

		return desc;
	}

	function statsRxWs(status, msg) {
		if (status !== COMM_OK) {
			console.error('statsRxWs: error ', status);
			return -1;
		}

		if (msg.cmd === 'obs') {
			if (msg.op === 'list') {
				if (msg.result === 'OK') {
					// console.log('statsRxWs: received', msg.data.obslist.length, 'observations.');
					gStorage.map.setHeader(statsGetStatsDesc());
					gStorage.map.setList(msg.data.obslist);
					navigate('/map');
				} else {
					console.error('statsRxWs: obslist query failed! Result: ', msg.result);
				}
			} else {
				console.error('statsRxWs: unknown operation', msg.cmd, msg.op, msg.result);
			}
		} else {
			console.error('statsRxWs: unknown command', msg.cmd, msg.op, msg.result);
		}
	}
	
	function loadTripObs() {
		const msg = {
			sess_id: gStorage.conn.sess_id,
			cmd: 'obs',
			op: 'list',
			data: {
				count: LIST_MAP_MAX_ELEM,
				pos: 0,
				city: [],
				species: [],
				trips: [gStorage.stats.trip],
				asso: [],
				tz: -new Date().getTimezoneOffset() * 60,
				sort_by: 'time',
				sort_order: 'desc',
			},
		}

		gStorage.conn.ws.send(msg, statsRxWs);
	}

	function toMap() {
		// console.log('stats: toMap');

		if (gStorage.stats.tab !== TAB_OTHER || gStorage.stats.other_tab !== TAB_TRIP) {
			gStorage.map.setList(gStorage.stats.obsList);
			gStorage.map.setHeader(statsGetStatsDesc());
			navigate('/map');
		} else {
			loadTripObs();
		}
	}

	/* ------------------------------------------------------------------ */

// 	console.log('Stats, tab:', gStorage.stats.tab);

	return (
		<div>
			<ObsAppBar {...props} getheader={getheader} enter_map={toMap}/>
			<Grid container direction="row" justifyContent="center" spacing={0} columns={1}>
				<Tabs
					id='statstab'
					value={gStorage.stats.tab} 
					onChange={handleTabChange} 
					align={'center'}
					indicatorColor="primary"
					textColor="primary"
					scrollButtons="auto"
					variant="scrollable"
					sx={{mb: 1}}>
					<Tab label={t('stats.missing')} disabled={!gStorage.conn.online}/>
					<Tab label={t('stats.time')} disabled={!gStorage.conn.online}/>
					<Tab label={t('stats.location')} disabled={!gStorage.conn.online}/>
					<Tab label={t('stats._species')} disabled={!gStorage.conn.online}/>
					<Tab label={t('stats.other')} disabled={!gStorage.conn.online}/>
					<Tab label={t('stats.obs')} disabled={!gStorage.conn.online || gStorage.main.n_obs <= 0 || gStorage.main.n_obs > LIST_MAP_MAX_ELEM}/>
					<Tab label={t('stats.compare')} disabled={!gStorage.stats.leftList.list.length || !gStorage.stats.rightList.list.length}/>
				</Tabs>
			</Grid>

			<StatsMissing />
			<StatsTime />
			<StatsLocation />
			<StatsSpecies />
			<StatsOther/>
			<StatsObs/>
			<StatsCompare/>
		</div>
	);
});

export default Stats;
